import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Box, Heading, Text, Button, TextLink } from '@indeed/ifl-components';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import PageTransition from '../../../components/PageTransition/PageTransition';

import {
    useGetScreenerQuestionsDataQuery,
    useUpdateLightApplyScreenerQuestionsMutation,
} from './__generated__/ScreenerQuestionsPage.generated';
import {
    updateSmartApplyQuestionsCache,
    updateSmartApplyQuestionCache,
} from '../../../lib/apollo/smartApplyStateCache';
import {
    hasSmartApplyScreenerAnswers,
    convertDraftAnswersToSmartApplyFormat,
    getQuestionStructure,
    getSmartApplyAnswers,
} from './screenerQuestionsDataUtilities';
import ScreenerQuestion from './ScreenerQuestion';

/**
 * ScreenerQuestionsPage with Smart Data Initialization
 *
 * Single Source of Truth: SmartApplyApplicationState
 *
 * Data Flow:
 * 1. Query both findApplicationDrafts (question structure) and SmartApplyApplicationState (answers)
 * 2. On page load, if SmartApply has no answers but findApplicationDrafts does,
 *    copy answers from findApplicationDrafts to SmartApply cache
 * 3. Use SmartApplyApplicationState as single source of truth for all operations
 *
 * Based on patterns from smart-apply-questions-onegraph-api and OneGraph ecosystem
 */

// No local state needed - Apollo cache is the single source of truth

const ScreenerQuestionsPage: React.FC = () => {
    const { goForward } = usePageFlow();
    const apolloClient = useApolloClient();

    // State to track when user has attempted to continue (for validation UX)
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    // IDs for both data sources - in a real app, these would come from URL params or context
    const applicationDraftId = 'apply_draft_123';
    const smartApplyNodeId = 'apply_state_123';

    // 1. Get server data from both sources - Initial load
    const { data, loading } = useGetScreenerQuestionsDataQuery({
        variables: {
            filterInput: {
                applicationDraftId,
            },
            nodeId: smartApplyNodeId,
        },
        skip: !applicationDraftId || !smartApplyNodeId,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network', // This will re-fetch when cache changes
        notifyOnNetworkStatusChange: true, // This ensures updates trigger re-renders
    });

    // 2. Server mutation for questions updates
    const [updateServerQuestions, { loading: saving }] =
        useUpdateLightApplyScreenerQuestionsMutation();

    // 3. Check if we need to initialize SmartApply from findApplicationDrafts data
    useEffect(() => {
        if (!data || loading) return;

        const hasSmartApplyAnswers = hasSmartApplyScreenerAnswers(data);

        if (!hasSmartApplyAnswers) {
            // Convert and update cache with findApplicationDrafts answers
            const draftAnswers = convertDraftAnswersToSmartApplyFormat(data);

            if (draftAnswers.length > 0) {
                updateSmartApplyQuestionsCache(apolloClient.cache, smartApplyNodeId, {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    screener: draftAnswers as any,
                });
            }
        }
    }, [data, loading, smartApplyNodeId, apolloClient.cache]);

    // 4. Extract data for display (SmartApply is now single source of truth)
    const questionStructure = useMemo(() => getQuestionStructure(data), [data]);
    const smartApplyAnswers = useMemo(() => getSmartApplyAnswers(data), [data]);
    const smartApplyState =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    // 6. Dynamic form validation based on questions from findApplicationDrafts
    const validateAnswers = useMemo(() => {
        const requiredQuestions = questionStructure.filter(
            (q) => q.requirement === 'REQUIRED' || q.requirement === 'required',
        );

        return requiredQuestions.every((question) => {
            const answer = smartApplyAnswers[question.questionId];
            return answer && answer.trim() !== '';
        });
    }, [questionStructure, smartApplyAnswers]);

    const canContinue = validateAnswers;

    // 7. Handle Continue - save questions to server
    const handleNext = async () => {
        if (!smartApplyState?.id) {
            console.error('Cannot save: missing SmartApplyApplicationState ID');
            return;
        }

        // Validation - check if all required questions are answered
        if (!validateAnswers) {
            // Show validation errors now that user has attempted to continue
            setHasAttemptedContinue(true);
            return;
        }

        try {
            // Map current answers to GraphQL screener format - dynamically from all answered questions
            const screenerAnswers = Object.entries(smartApplyAnswers)
                .filter(([, answer]) => answer && answer.trim() !== '')
                .map(([questionId, answer]) => ({
                    questionId,
                    answer: { value: answer },
                }));

            const mutationVariables = {
                input: { id: smartApplyState.id },
                updateQuestionsInput: {
                    applicationDraftId: smartApplyState.draftId, // Required field
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    screener: screenerAnswers as any,
                },
            };

            // Send questions to server
            await updateServerQuestions({
                variables: mutationVariables,
            });

            // Navigate to next page
            void goForward();
        } catch (error) {
            console.error('Failed to save screener questions:', error);
            // Could show error message to user
        }
    };

    // 8. Question change handler for the ScreenerQuestion component - Clean & Simple
    const handleQuestionChange = (questionId: string, value: string) => {
        // Prevent cache updates while data is still loading
        if (loading) {
            return;
        }

        if (!smartApplyState?.id) {
            console.warn('⚠️  Cannot update cache: missing SmartApplyApplicationState ID');
            return;
        }

        // Use centralized cache update function - all complexity handled in smartApplyStateCache.ts
        const updatedQuestionsState = updateSmartApplyQuestionCache(
            apolloClient.cache,
            smartApplyState.id,
            questionId,
            value,
        );

        if (!updatedQuestionsState) {
            console.error(`Failed to update question: ${questionId}`);
        }
    };

    if (loading) {
        return (
            <PageTransition pageKey="light-apply-questions">
                <Helmet>
                    <title>採用企業からの質問に回答してください - Light Apply - Indeed</title>
                </Helmet>

                <Box sx={{ padding: '32px 16px', textAlign: 'center' }}>
                    <Text>質問情報を読み込んでいます...</Text>
                </Box>
            </PageTransition>
        );
    }

    return (
        <PageTransition pageKey="light-apply-questions">
            <Helmet>
                <title>採用企業からの質問に回答してください - Light Apply - Indeed</title>
            </Helmet>

            <Box sx={{ inlineSize: '100%' }}>
                <Box sx={{ marginBlockEnd: '32px' }}>
                    <Heading level={2} sx={{ marginBlockEnd: '8px', fontSize: '24px' }}>
                        採用企業からの質問に回答してください
                    </Heading>
                    <Text sx={{ color: 'secondary', marginBlockEnd: '16px' }}>
                        こちらは採用企業からの質問です。不適切と思われる質問があった場合、
                        <TextLink
                            href="https://jp.indeed.com"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            問題のある求人を報告する
                        </TextLink>
                        ことができます。
                    </Text>
                </Box>

                <Box
                    sx={{
                        marginBlockEnd: '32px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    {questionStructure.length > 0 ? (
                        <Box
                            sx={{
                                marginBlockEnd: '32px',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '32px',
                            }}
                        >
                            {questionStructure.map((question) => {
                                const currentAnswerValue =
                                    smartApplyAnswers[question.questionId] || '';
                                const isRequired =
                                    question.requirement === 'REQUIRED' ||
                                    question.requirement === 'required';
                                const hasError =
                                    hasAttemptedContinue &&
                                    isRequired &&
                                    !currentAnswerValue.trim();

                                return (
                                    <ScreenerQuestion
                                        key={question.questionId}
                                        question={question}
                                        currentAnswerValue={currentAnswerValue}
                                        hasError={hasError}
                                        onQuestionChange={handleQuestionChange}
                                    />
                                );
                            })}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', padding: '32px', color: 'secondary' }}>
                            <Text>質問が見つかりませんでした。</Text>
                        </Box>
                    )}

                    <Box>
                        <Button
                            variant="primary"
                            size="lg"
                            sx={{ inlineSize: '100%', marginBlockEnd: '16px' }}
                            onClick={handleNext}
                            disabled={!canContinue || saving}
                        >
                            {saving ? '保存中...' : '次へ'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ScreenerQuestionsPage;
